import OvenPlayer, { OvenPlayerEvents } from 'ovenplayer';
import { useEffect, useRef, useState } from 'react';

// ovenplayerinstance is not exposed by the type definition
type OvenPlayerInstance = ReturnType<typeof OvenPlayer.create>;
// Reconnect quickly so playback resumes the moment OME has the stream again.
const RELOAD_MS = 200;
// Outage length after which the slate says the stream is down.
const STALL_MS = 20 * 1000;
// Sample WebRTC stats at 1 Hz: matches the browser's internal ~1s stats update cadence,
// gives clean bytesReceived deltas for bitrate, and keeps main-thread cost negligible.
// Sampling faster mostly duplicates values at higher render/CPU cost.
const STATS_SAMPLE_INTERVAL_MS = 1000;

// TEMPORARY build check — runs once when this module is imported (game page load).
// If you don't see this line in the console, the running frontend is NOT this build.
console.debug('[webrtc] usePlayer module loaded — WebRTC metrics logging is present');

export type PlayerHandlers = {
  peerConnectionPrepared?: (myPeerConnection: RTCPeerConnection) => void;
  stateChanged?: (e: OvenPlayerEvents['stateChanged']) => void;
};

export type UsePlayerParams = {
  elId: string;
  sources: Array<{ type: "webrtc" | "llhls" | "hls" | "lldash" | "dash" | "mp4"; file: string }>;
  handlers?: PlayerHandlers;
  onWebrtcStatsChange?: (stats: any) => void;
  onReady?: (peerConnection: RTCPeerConnection) => void;
};

const usePlayer = ({
  elId,
  sources,
  handlers = {},
  onWebrtcStatsChange,
  onReady,
}: UsePlayerParams) => {
  // Bumped to recreate the player after an error; a plain counter can't bounce.
  const [reloadNonce, setReloadNonce] = useState(0);
  // True after STALL_MS without playback, timed from the first dial or the
  // interruption (a dial can hang until connectionTimeout). Cleared on playing or paused.
  const [stalled, setStalled] = useState(false);
  const reloadAttemptsRef = useRef(0);
  const stallTimerRef = useRef<NodeJS.Timeout | null>(null);
  const startOutage = () => {
    if (!stallTimerRef.current) stallTimerRef.current = setTimeout(() => setStalled(true), STALL_MS);
  };
  const reloadTimerRef = useRef<NodeJS.Timeout | null>(null);

  const playerRef = useRef<OvenPlayerInstance>(null);
  const loadStatsTimerRef = useRef<NodeJS.Timeout | null>(null);

  // The stats interval is created once (when the peer connection is prepared), but the
  // consumer callback may change on re-render. Route through a ref so the interval always
  // invokes the latest callback instead of the one captured at setup time.
  const onWebrtcStatsChangeRef = useRef(onWebrtcStatsChange);
  onWebrtcStatsChangeRef.current = onWebrtcStatsChange;

  if (!handlers.peerConnectionPrepared) {
    handlers.peerConnectionPrepared = (peerConnection: RTCPeerConnection) => {
      if (onReady) onReady(peerConnection);

      // TEMPORARY debug tracing (unconditional console.log so it shows at the default level).
      // Seeing this line confirms a WebRTC peer connection exists (i.e. not an HLS fallback).
      console.debug(
        `[webrtc:player] peerConnectionPrepared — sampling getStats() every ${STATS_SAMPLE_INTERVAL_MS}ms`,
      );

      loadStatsTimerRef.current = setInterval(async () => {
        try {
          const stats = await peerConnection.getStats();

          console.debug(`[webrtc:player] getStats() tick — ${stats?.size ?? 0} entries`);

          onWebrtcStatsChangeRef.current?.(stats);
        } catch (e) {
          console.error('[webrtc:player] getStats() failed', e);
          return;
        }
      }, STATS_SAMPLE_INTERVAL_MS);
    };
  }


  // The player is created imperatively, so `sources` itself is not a dep — but the
  // URLs can arrive late (the session host comes from an SSE event), so key the
  // effect on them. Together with the early return below, this means we create the
  // player exactly once we have real URLs, instead of dialing a half-built one.
  const sourcesKey = sources.map((source) => source.file).join('|');

  useEffect(() => {
    if (sources.length === 0) {
      return;
    }

    function createPlayer() {
      return OvenPlayer.create(elId, {
        autoFallback: true,
        autoStart: true,
        controls: false,
        disableSeekUI: true,
        expandFullScreenUI: false,
        mute: true,
        showBigPlayButton: false,
        sources,
        webrtcConfig: {
          timeoutMaxRetry: 10,
          connectionTimeout: 20000
        },
      });
    }

    playerRef.current = createPlayer();
    startOutage();

    playerRef.current.on('peerConnectionPrepared', handlers.peerConnectionPrepared);
    // Until this instance has actually played, swallow its 'loading' blips:
    // every dial attempt reports loading for an instant, and forwarding that
    // makes the slate blink on each retry while the stream comes up.
    let played = false;
    playerRef.current.on('stateChanged', (e) => {
      if (e.newstate === 'playing') {
        played = true;
        reloadAttemptsRef.current = 0;
      }
      if (e.newstate === 'playing' || e.newstate === 'paused') {
        if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
        stallTimerRef.current = null;
        setStalled(false);
      } else if (played) {
        startOutage();
      }
      if (e.newstate === 'loading' && !played) return;
      handlers.stateChanged?.(e);
    });

    playerRef.current.once('error', (error) => {
      const attempt = reloadAttemptsRef.current++;
      if (attempt === 0 || attempt % 20 === 0) console.warn('[player] dial failed, retrying', error);
      // The failed instance stays mounted until the retry replaces it in the
      // same tick (removing it here left a layout gap that blinked the slate);
      // its error overlay is hidden by CSS in GamePageContents.
      reloadTimerRef.current = setTimeout(() => setReloadNonce((n) => n + 1), RELOAD_MS);
    });

    return () => {
      if (reloadTimerRef.current) {
        clearTimeout(reloadTimerRef.current);
        reloadTimerRef.current = null;
      }
      if (loadStatsTimerRef.current) {
        clearInterval(loadStatsTimerRef.current);
      }

      try {
        if (playerRef.current == null) {
          return;
        }
        playerRef.current.remove();
      } catch (e) {
        console.warn('failed to destroy the player ... ');
      }
    };
  }, [elId, reloadNonce, sourcesKey]);

  useEffect(() => () => {
    if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
    stallTimerRef.current = null;
  }, []);

  return {
    player: playerRef.current,
    stalled,
  };
};

export default usePlayer;
